import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Table,
  Typography,
  Tag,
  Tabs,
  Badge,
  Button,
  theme,
  message,
} from "antd";
import {
  CheckCircleOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import PageLayout from "@components/pageLayout/PageLayout.react";
import { authenticatedFetch } from "@/libs/api";
import { makePagination } from "@components/common/TablePagination";

const { Title, Text } = Typography;

const tableHeaderStyle = `
  .system-status-table .ant-table-thead > tr > th {
    white-space: nowrap;
  }
  .system-status-table .ant-table-thead > tr > th .ant-table-column-title {
    display: inline-flex;
    align-items: center;
    min-height: 22px;
  }
`;

const VPP_FCAS_LABELS = {
  0: "Contact Admin",
  1: "Never Online",
  2: "Version 1.2.6",
  3: "NO CT",
  4: "Lost Connection With Inverter",
  5: "Offline for a long time",
  6: "Can't OTA, need restart",
  7: "Need Upgrade",
  8: "Registered",
  9: "Ready For Registered",
  10: "NO CT & Lost Connection With Inverter",
};

const VPP_FCAS_COLORS = {
  0: "red",
  1: "red",
  2: "red",
  3: "orange",
  4: "orange",
  5: "orange",
  6: "gold",
  7: "gold",
  8: "blue",
  9: "green",
  10: "red",
};

export default function SystemStatus() {
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("all");
  const [allSystems, setAllSystems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageSize, setPageSize] = useState(15);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [groupOptions, setGroupOptions] = useState([]);
  const [activeFilters, setActiveFilters] = useState({});
  const activeFiltersRef = useRef({});
  const [sortState, setSortState] = useState({ field: null, order: null });
  const sortStateRef = useRef({ field: null, order: null });
  const [exportLoading, setExportLoading] = useState(false);

  const roleType = localStorage.getItem("roleType") || "";
  const isSuperadmin = roleType === "SUPERADMIN";
  const userPermissions = (() => {
    try { return JSON.parse(localStorage.getItem("permissions") || "[]"); }
    catch { return []; }
  })();
  const canExport = isSuperadmin || userPermissions.includes("system:export");
  const isProtectedView = !isSuperadmin && userPermissions.includes("system:protected_view") && !userPermissions.includes("system:read");

  const fetchSystems = async (page = 1, size = 15, filters = {}, sort = sortStateRef.current) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, pageSize: size });
      if (filters.group?.length) filters.group.forEach((v) => params.append("group", v));
      if (filters.controllerName?.length) filters.controllerName.forEach((v) => params.append("controllerName", v));
      if (filters.vppFcasStatus?.length) filters.vppFcasStatus.forEach((v) => params.append("vppFcasStatus", v));
      if (filters.status?.length) filters.status.forEach((v) => params.append("status", v));
      // The Status column (connectionStatus) is filtered server-side against the
      // whole fleet, same as the other columns — see backend/src/controllers/systemController.js.
      if (filters.connectionStatus?.length) filters.connectionStatus.forEach((v) => params.append("connectionStatus", v));
      if (sort?.field && sort?.order) {
        params.append("sortField", sort.field);
        params.append("sortOrder", sort.order);
      }

      const response = await authenticatedFetch(`/api/systems?${params.toString()}`);
      const result = await response.json();

      if (result.success) {
        const systems = result.data.map((s) => ({
          id: s.id,
          sn: s.sn,
          nmi: s.nmi || "-",
          group: s.control_group_name || s.view_group_name || "Unassigned",
          status: s.status || "ACTIVE",
          pvKw: s.pv_kw ? parseFloat(s.pv_kw).toFixed(1) : "-",
          inverterKw: s.inverter_kw ? parseFloat(s.inverter_kw).toFixed(1) : "-",
          batteryKwh: s.battery_kwh ? parseFloat(s.battery_kwh).toFixed(1) : "-",
          inverterName: s.inverter_name || "-",
          batteryName: s.battery_name || "-",
          controllerName: s.controller_name || "-",
          customerName: s.customer_name || "-",
          customerEmail: s.customer_email || "-",
          onboardDate: s.onboard_date || "-",
          companyName: s.company_name || "-",
          vppFcasStatus: s.vpp_fcas_status ?? null,
          connectionStatus: s.connection_status || (s.online === 1 || s.online === true ? "online" : "no_data"),
          lastSeen: s.last_seen || null,
          soc: s.soc != null ? Number(s.soc) : null,
          uptime: s.uptime || null,
        }));
        setAllSystems(systems);
        setTotal(result.total);
        setCurrentPage(page);
        if (result.groupOptions) setGroupOptions(result.groupOptions);
      } else {
        message.error(result.message || "Failed to fetch systems");
      }
    } catch (error) {
      console.error("Error fetching systems:", error);
      message.error("Failed to fetch systems");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSystems(1, pageSize);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRowClick = (record) => {
    navigate(`/systems/${record.id}`);
  };

  // Group filter options come from the API (all accessible groups, not just current page)
  const groupFilters = groupOptions.map((g) => ({ text: g, value: g }));

  // All systems columns
  const allColumns = [
    {
      title: "Serial Number",
      dataIndex: "sn",
      key: "sn",
      render: (sn) => <Text code style={{ fontSize: 12, cursor: "pointer" }}>{sn}</Text>,
    },
    ...(!isProtectedView ? [{
      title: "NMI",
      dataIndex: "nmi",
      key: "nmi",
      render: (nmi) => <Text type="secondary" style={{ fontSize: 12 }}>{nmi}</Text>,
    }] : []),
    {
      title: "Customer",
      dataIndex: "customerName",
      key: "customerName",
      render: (name) => <Text>{isProtectedView ? (name && name !== "-" ? name.split(" ")[0] : name) : name}</Text>,
    },
    {
      title: "Group",
      dataIndex: "group",
      key: "group",
      filters: groupFilters,
      filteredValue: activeFilters.group || null,
      render: (group) => <Tag color={group === "Unassigned" ? "default" : "blue"}>{group}</Tag>,
    },
    {
      title: "Control Device",
      dataIndex: "controllerName",
      key: "controllerName",
      filters: [
        { text: "Alpha ESS", value: "Alpha ESS" },
        { text: "Powow Meter", value: "Powow Meter" },
      ],
      filteredValue: activeFilters.controllerName || null,
      render: (name) => <Text>{name}</Text>,
    },
    {
      title: "Status",
      dataIndex: "connectionStatus",
      key: "connectionStatus",
      filters: [
        { text: "Online", value: "online" },
        { text: "Offline", value: "offline" },
        { text: "No Data", value: "no_data" },
      ],
      // No `onFilter` — deliberately. Ant Design only runs client-side
      // filtering (against whatever rows happen to be loaded on the current
      // page) when a column defines `onFilter`. This column is filtered
      // server-side, against the full fleet, exactly like Group/Control
      // Device/VPP Status above — see fetchSystems' `connectionStatus` param
      // and backend/src/controllers/systemController.js.
      filteredValue: activeFilters.connectionStatus || null,
      render: (status) => (
        <Tag color={status === "online" ? "success" : status === "offline" ? "error" : "default"}>
          {status === "online" ? "Online" : status === "offline" ? "Offline" : "No Data"}
        </Tag>
      ),
    },
    {
      title: "VPP Status",
      dataIndex: "vppFcasStatus",
      key: "vppFcasStatus",
      filters: Object.entries(VPP_FCAS_LABELS).map(([code, label]) => ({ text: label, value: code })),
      filteredValue: activeFilters.vppFcasStatus?.map(String) || null,
      render: (code) => {
        if (code === null || code === undefined) return <Text type="secondary">N/A</Text>;
        return (
          <Tag color={VPP_FCAS_COLORS[code] ?? "default"}>
            {VPP_FCAS_LABELS[code] ?? `Status ${code}`}
          </Tag>
        );
      },
    },
    {
      title: "PV",
      dataIndex: "pvKw",
      key: "pvKw",
      // `sorter: true` (not a comparator function) — sorting happens
      // server-side against the whole fleet. A comparator function makes
      // Ant Design sort only the current page's rows in-place, which is
      // wrong once the fleet spans more than one page.
      sorter: true,
      sortOrder: sortState.field === "pvKw" ? sortState.order : null,
      render: (val) => <Text>{val !== "-" ? `${val} kW` : "-"}</Text>,
    },
    {
      title: "Inverter",
      dataIndex: "inverterKw",
      key: "inverterKw",
      sorter: true,
      sortOrder: sortState.field === "inverterKw" ? sortState.order : null,
      render: (val) => <Text>{val !== "-" ? `${val} kW` : "-"}</Text>,
    },
    {
      title: "Battery",
      dataIndex: "batteryKwh",
      key: "batteryKwh",
      sorter: true,
      sortOrder: sortState.field === "batteryKwh" ? sortState.order : null,
      render: (val) => <Text>{val !== "-" ? `${val} kWh` : "-"}</Text>,
    },
    {
      title: "Company",
      dataIndex: "companyName",
      key: "companyName",
      render: (name) => <Text type="secondary" style={{ fontSize: 12 }}>{name}</Text>,
    },
  ];

  const handleTableChange = (pagination, filters, sorter) => {
    // Normalize Ant Design filter object: remove null/empty entries
    const cleanFilters = {};
    Object.entries(filters).forEach(([key, values]) => {
      if (values && values.length > 0) cleanFilters[key] = values;
    });

    // Ant Design passes an array when multiple columns are sorted at once;
    // this table only ever has one sortable column active, so take the first.
    const s = Array.isArray(sorter) ? sorter[0] : sorter;
    const newSort = s?.order ? { field: s.field, order: s.order } : { field: null, order: null };

    const filtersChanged = JSON.stringify(cleanFilters) !== JSON.stringify(activeFiltersRef.current);
    const sortChanged = JSON.stringify(newSort) !== JSON.stringify(sortStateRef.current);
    const newPage = (filtersChanged || sortChanged) ? 1 : (pagination.current || currentPage);

    activeFiltersRef.current = cleanFilters;
    setActiveFilters(cleanFilters);
    sortStateRef.current = newSort;
    setSortState(newSort);
    fetchSystems(newPage, pageSize, cleanFilters, newSort);
  };

  const handleExportFleet = async () => {
    setExportLoading(true);
    try {
      const filters = activeFiltersRef.current;
      const params = new URLSearchParams({ page: 1, pageSize: 10000 });
      if (filters.group?.length) filters.group.forEach((v) => params.append("group", v));
      if (filters.controllerName?.length) filters.controllerName.forEach((v) => params.append("controllerName", v));
      if (filters.vppFcasStatus?.length) filters.vppFcasStatus.forEach((v) => params.append("vppFcasStatus", v));
      if (filters.status?.length) filters.status.forEach((v) => params.append("status", v));
      if (filters.connectionStatus?.length) filters.connectionStatus.forEach((v) => params.append("connectionStatus", v));
      if (sortStateRef.current?.field && sortStateRef.current?.order) {
        params.append("sortField", sortStateRef.current.field);
        params.append("sortOrder", sortStateRef.current.order);
      }

      const response = await authenticatedFetch(`/api/systems?${params.toString()}`);
      const result = await response.json();

      if (!result.success || result.data.length === 0) {
        message.warning("No systems to export");
        return;
      }

      const exportData = result.data.map((s) => ({
        sn: s.sn,
        nmi: s.nmi || "-",
        customerName: s.customer_name || "-",
        customerEmail: s.customer_email || "-",
        customerPhone: s.customer_phone || "-",
        // POW-187: per-system site address, not the shared customer address.
        customerAddress:
          s.addressline ||
          [s.street, s.suburb, s.state, s.postcode]
            .filter(Boolean).join(" ").trim() ||
          "-",
        customerPostcode: s.customer_postcode || "-",
        customerState: s.customer_state || "-",
        controllerName: s.controller_name || "-",
        companyName: s.company_name || "-",
        // Format as YYYY-MM-DD in Brisbane time. PG DATE columns come back as
        // ISO timestamps (e.g. "2026-05-03T14:00:00.000Z"); naive .slice(0,10)
        // would shift the date by one day in AEST/AEDT.
        onboardDate: s.onboard_date
          ? new Date(s.onboard_date).toLocaleDateString("en-CA", { timeZone: "Australia/Brisbane" })
          : "-",
        // Mirrors legacy fleet export: only show date when status=1, else empty.
        registeredDate: s.registered_status === 1 && s.registered_date
          ? new Date(s.registered_date).toLocaleDateString("en-CA", { timeZone: "Australia/Brisbane" })
          : "",
        registeredStatus: s.registered_status ?? 0,
        vppNote: VPP_FCAS_LABELS[s.vpp_fcas_status] || "-",
      }));

      const headers = ["Serial Number", "NMI", "Customer", "Customer Email", "Customer Phone", "Address", "Postcode", "State", "Controller", "Company", "Onboard Date", "Registered Date", "Registered Status", "VPP Note"];
      const rows = exportData.map((s) => [
        s.sn, s.nmi, s.customerName, s.customerEmail, s.customerPhone, s.customerAddress, s.customerPostcode, s.customerState,
        s.controllerName, s.companyName, s.onboardDate, s.registeredDate, s.registeredStatus, s.vppNote,
      ]);
      const csvContent = [headers, ...rows]
        .map((row) => row.map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`).join(","))
        .join("\n");
      const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `fleet_export_${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      URL.revokeObjectURL(url);
      message.success(`Exported ${exportData.length} systems`);
    } catch (error) {
      console.error("Export error:", error);
      message.error("Failed to export systems");
    } finally {
      setExportLoading(false);
    }
  };

  const tabItems = [
    {
      key: "all",
      label: (
        <span>
          <CheckCircleOutlined style={{ color: token.colorPrimary }} />
          All Systems
          <Badge count={total} style={{ marginLeft: 8, backgroundColor: token.colorPrimary }} />
        </span>
      ),
      children: (
        <>
        <style>{tableHeaderStyle}</style>
        <Table
          className="system-status-table"
          columns={allColumns}
          dataSource={allSystems}
          rowKey="id"
          loading={loading}
          pagination={makePagination(pageSize, (size) => { setPageSize(size); fetchSystems(1, size, activeFiltersRef.current); }, {
            current: currentPage,
            total,
          })}
          size="middle"
          scroll={{ x: "max-content" }}
          onChange={handleTableChange}
          onRow={(record) => ({ onClick: () => handleRowClick(record), style: { cursor: "pointer" } })}
          locale={{ emptyText: "No systems found. Onboard systems to see them here." }}
        />
        </>
      ),
    },
  ];

  return (
    <PageLayout active="systemsSummary">
      <div style={{ marginBottom: 24, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <Title level={2} style={{ marginBottom: 4 }}>Systems Summary</Title>
          <Text type="secondary" style={{ fontSize: 14 }}>
            View all onboarded systems. Click on a system to view details and manage offboarding.
          </Text>
        </div>
        {canExport && (
          <Button
            icon={<DownloadOutlined />}
            onClick={handleExportFleet}
            loading={exportLoading}
            disabled={loading || total === 0}
          >
            Export Fleet
          </Button>
        )}
      </div>

      <Card bordered={false} style={{ borderRadius: 12 }}>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
          size="large"
        />
      </Card>
    </PageLayout>
  );
}
